import React, { useCallback, useRef, useState } from 'react';
import { Box, IconButton, Tooltip } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import type { InkStroke } from './blocks/InkBlockNode';
import { rasterizeInk, toBase64Payload } from '../../utils/inkRasterizer';
import { interpretInk } from '../../services/gnInkService';
import type { GnRole } from './GnContext';

interface InkLayerProps {
  role: GnRole;
  /** Committed once pen mode is turned off (or the layer unmounts while drawing) — spec 046 FR-002. */
  onCommitStrokes: (strokes: InkStroke[], width: number, height: number, interpretedText: string, confidence: number) => void;
}

const WIDTH = 700;
const HEIGHT = 260;

/**
 * Freehand pen capture (spec 046 FR-002). On devices without pen support this toggle simply
 * accepts mouse/touch too rather than being hidden — the important FR-002 guarantee is that
 * ALL OTHER capabilities work unaffected when pen mode is off, which holds here since this
 * is an opt-in overlay, not something imposed on the base editing experience.
 */
const InkLayer: React.FC<InkLayerProps> = ({ role, onCommitStrokes }) => {
  const [active, setActive] = useState(false);
  const [strokes, setStrokes] = useState<InkStroke[]>([]);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const currentPointsRef = useRef<number[]>([]);
  const drawingRef = useRef(false);

  const redraw = useCallback((allStrokes: InkStroke[], live?: number[]) => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = '#1a1a1a';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    const drawStroke = (points: number[]) => {
      if (points.length < 4) return;
      ctx.beginPath();
      ctx.moveTo(points[0], points[1]);
      for (let i = 2; i < points.length; i += 2) ctx.lineTo(points[i], points[i + 1]);
      ctx.stroke();
    };
    allStrokes.forEach(s => drawStroke(s.points));
    if (live) drawStroke(live);
  }, []);

  const getRelativePoint = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    return [e.clientX - rect.left, e.clientY - rect.top];
  };

  const handlePointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    drawingRef.current = true;
    currentPointsRef.current = getRelativePoint(e);
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawingRef.current) return;
    currentPointsRef.current.push(...getRelativePoint(e));
    redraw(strokes, currentPointsRef.current);
  };

  const handlePointerUp = () => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    if (currentPointsRef.current.length >= 4) {
      const newStrokes = [...strokes, { points: currentPointsRef.current }];
      setStrokes(newStrokes);
      redraw(newStrokes);
    }
    currentPointsRef.current = [];
  };

  const commit = useCallback(async () => {
    if (strokes.length === 0) return;
    let interpretedText = '';
    let confidence = 0;
    try {
      const dataUrl = rasterizeInk(strokes, WIDTH, HEIGHT);
      const result = await interpretInk(role, toBase64Payload(dataUrl));
      interpretedText = result.text;
      confidence = result.confidence;
    } catch {
      // interpretation failure just means a low-confidence, uninterpreted ink block — the
      // ink itself (the important part, FR-002) is committed regardless.
    }
    onCommitStrokes(strokes, WIDTH, HEIGHT, interpretedText, confidence);
    setStrokes([]);
  }, [strokes, role, onCommitStrokes]);

  const togglePen = () => {
    if (active) {
      commit();
    }
    setActive(a => !a);
  };

  return (
    <>
      <Tooltip title={active ? 'Finish writing (saves your ink)' : 'Write with pen'}>
        <IconButton size="small" onClick={togglePen} color={active ? 'primary' : 'default'} data-testid="gn-pen-toggle">
          <EditIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      {active && (
        <Box
          sx={{ border: '1px dashed', borderColor: 'primary.main', borderRadius: 1, my: 1, display: 'inline-block' }}
          data-testid="gn-ink-layer"
        >
          <canvas
            ref={canvasRef}
            width={WIDTH}
            height={HEIGHT}
            style={{ touchAction: 'none', display: 'block', cursor: 'crosshair' }}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerLeave={handlePointerUp}
          />
        </Box>
      )}
    </>
  );
};

export default InkLayer;
